<template>
  <div flat class="transparent-search">
    <div class="search-container">
      <!-- STEP INDICATOR -->
      <div class="step-indicator" v-if="showSteps">
        <div class="step-dots">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            class="step-dot"
            :class="{
              'active': currentStep === index,
              'completed': currentStep > index,
              'clickable': currentStep >= index
            }"
            @click="goToStep(index)"
          >
            <span class="step-number">{{ index + 1 }}</span>
            <span class="step-label">{{ step.label }}</span>
          </div>
        </div>
        <v-progress-linear 
          :value="((currentStep + 1) / steps.length) * 100"
          color="primary"
          height="3"
          class="step-progress"
          rounded
        ></v-progress-linear>
      </div>

      <v-layout row wrap fluid v-if="['Index', 'MakeGuestBooking'].includes($route.name)">
        <!-- STEP 1: CHECK-IN -->
        <v-flex lg2 md2 sm12>
          <v-container>
            <div class="step-wrapper" :class="{ 'step-highlight': currentStep === 0 }">
              <div class="step-badge" v-if="currentStep === 0">1</div>
              <v-dialog ref="dialog" persistent v-model="modal1" lazy full-width width="290px" :return-value.sync="booking.check_in">
                <template v-slot:activator="{ on }">
                  <v-text-field 
                    v-on="on" 
                    label="CHECK-IN" 
                    v-model="booking.check_in" 
                    readonly 
                    solo 
                    flat 
                    class="earthwerk-font input-field" 
                    background-color="rgba(255,255,255,0.9)"
                    @focus="setCurrentStep(0)"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="booking.check_in" :min="start_date" @input="onCheckInSelected" scrollable>
                  <v-spacer></v-spacer>
                  <v-btn flat color="primary" @click="modal1 = false" class="earthwerk-font">CANCEL</v-btn>
                  <v-btn flat color="primary" @click="saveCheckIn" class="earthwerk-font">OK</v-btn>
                </v-date-picker>
              </v-dialog>
              <div class="step-hint" v-if="currentStep === 0">📅 Select your check-in date</div>
            </div>
          </v-container>
        </v-flex>

        <!-- STEP 2: CHECK-OUT -->
        <v-flex lg2 md2 sm12>
          <v-container>
            <div class="step-wrapper" :class="{ 'step-highlight': currentStep === 1 }">
              <div class="step-badge" v-if="currentStep === 1">2</div>
              <v-dialog ref="dialog1" persistent v-model="modal2" lazy full-width width="290px" :return-value.sync="booking.check_out">
                <template v-slot:activator="{ on }">
                  <v-text-field 
                    v-on="on" 
                    label="CHECK-OUT" 
                    v-model="booking.check_out" 
                    readonly 
                    solo 
                    flat 
                    class="earthwerk-font input-field" 
                    background-color="rgba(255,255,255,0.9)"
                    @focus="setCurrentStep(1)"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="booking.check_out" :min="minCheckOutDate" @input="onCheckOutSelected" scrollable>
                  <v-spacer></v-spacer>
                  <v-btn flat color="primary" @click="modal2 = false" class="earthwerk-font">CANCEL</v-btn>
                  <v-btn flat color="primary" @click="saveCheckOut" class="earthwerk-font">OK</v-btn>
                </v-date-picker>
              </v-dialog>
              <div class="step-hint" v-if="currentStep === 1">📅 Select your check-out date</div>
            </div>
          </v-container>
        </v-flex>

        <!-- STEP 3: GUESTS -->
        <v-flex lg2 md2 sm12 v-if="booking.check_in && booking.check_out">
          <v-container>
            <div class="step-wrapper" :class="{ 'step-highlight': currentStep === 2 }">
              <div class="step-badge" v-if="currentStep === 2">3</div>
              <guests 
                class="earthwerk-font" 
                @guests-auto-updated="handleGuestsUpdated"
                @focus="setCurrentStep(2)"
              ></guests>
              <div class="step-hint" v-if="currentStep === 2">👥 Add your guests</div>
            </div>
          </v-container>
        </v-flex>

        <!-- STEP 4: VEHICLES -->
        <v-flex lg2 md2 sm12>
          <v-container>
            <div class="step-wrapper" :class="{ 'step-highlight': currentStep === 3 }">
              <div class="step-badge" v-if="currentStep === 3">4</div>
              <vehicle 
                class="earthwerk-font" 
                @vehicles-auto-updated="handleVehiclesUpdated"
                @focus="setCurrentStep(3)"
              ></vehicle>
              <div class="step-hint" v-if="currentStep === 3">🚗 Select your vehicles</div>
            </div>
          </v-container>
        </v-flex>

        <!-- STEP 5: VIEW CART - HIDDEN UNTIL GUESTS AND VEHICLES SELECTED -->
        <v-flex lg4 md4 sm12 v-if="showViewCartButton">
          <v-container>
            <div class="step-wrapper" :class="{ 'step-highlight': currentStep === 4 }">
              <div class="step-badge" v-if="currentStep === 4">5</div>
              <v-btn 
                block 
                color="primary" 
                dark 
                @click="viewCart()" 
                class="earthwerk-font action-button"
                @mouseenter="setCurrentStep(4)"
              >
                <v-icon left small>mdi-cart</v-icon>
                VIEW CART 
                <v-chip v-if="cartTotal > 0" small color="white" text-color="primary" class="ml-2">{{ cartTotal }}</v-chip>
              </v-btn>
              <div class="step-hint" v-if="currentStep === 4">🛒 Review your selections</div>
            </div>
          </v-container>
        </v-flex>

        <v-snackbar :multi-line="true" :left="true" :vertical="true" :timeout="6000" color="error" v-model="snackbar">
          <span class="earthwerk-font">CHECK-IN AND CHECK-OUT DATES MUST BE PROVIDED</span>
          <v-btn flat @click="snackbar = false" class="earthwerk-font">CANCEL</v-btn>
        </v-snackbar>
      </v-layout>

      <!-- SIMPLE ARROW GUIDE - Appears after step 5, Clickable to scroll to tabs -->
      <div class="arrow-guide" v-if="showSteps && currentStep === 4" @click="scrollToTabs">
        <div class="arrow-down">
          <span class="arrow-icon">▼</span>
          <span class="arrow-text earthwerk-font">Browse Activities, Accommodation &amp; Camping below and add to cart</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Guests from './Guests'
  import Vehicle from './Vehicle'

  export default {
    name: 'Search',
    components: { Vehicle, Guests },
    data() {
      return {
        modal1: false,
        modal2: false,
        snackbar: false,
        start_date: this.$moment().format('YYYY-MM-DD'),
        minCheckOutDate: this.$moment().format('YYYY-MM-DD'),
        route: this.$route.name,
        cartTotal: 0,
        currentStep: 0,
        showSteps: true,
        showViewCartButton: false,
        hasGuests: false,
        hasVehicles: false,
        vehicleFeeSeparate: false,
        steps: [
          { label: 'Check-in', icon: '📅' },
          { label: 'Check-out', icon: '📅' },
          { label: 'Guests', icon: '👥' },
          { label: 'Vehicles', icon: '🚗' },
          { label: 'View Cart', icon: '🛒' }
        ],
        booking: {
          days: 0,
          check_in: '',
          check_out: '',
          currency: localStorage.getItem('currency')
        }
      }
    },
    computed: {
      currency: function () {
        return this.$store.state.CurrencyStore.browser_currency
      },
      guestsSelected: function () {
        const guests = this.$store.state.GuestsStore ? this.$store.state.GuestsStore.guests : null
        return guests !== 'Number of Guests' && Number(guests) > 0
      },
      globalBookingDates: function() {
        return this.$store.state.GlobalBookingStore ? this.$store.state.GlobalBookingStore.booking_dates : null
      }
    },
    created() {
      const isPageRefresh = performance.navigation.type === 1;
      if (isPageRefresh) {
        this.clearAllBookingData();
      }
      
      this.loadDatesFromGlobalStore()
      
      if (!this.booking.check_in && localStorage.getItem('booking') !== null) {
        var booking = JSON.parse(localStorage.getItem('booking'))
        this.booking = booking
        this.updateCheckOutMin()
        this.saveDatesToGlobalStore()
      }
      
      this.$store.dispatch('get_all_currencies')
      
      this.updateCartTotal()
      
      this.showSteps = this.$route.name === 'Index'
      
      this.checkViewCartButtonVisibility()
    },
    beforeDestroy() {
    },
    methods: {
      // ============ STEP NAVIGATION ============
      setCurrentStep(step) {
        this.currentStep = step
      },
      
      goToStep(step) {
        if (step <= this.currentStep) {
          this.currentStep = step
        }
      },
      
      advanceStep() {
        if (this.currentStep < this.steps.length - 1) {
          this.currentStep++
        }
      },
      
      // ============ VIEW CART BUTTON VISIBILITY ============
      checkViewCartButtonVisibility() {
        // Check guests from localStorage
        try {
          const guestsData = JSON.parse(localStorage.getItem('guests') || '{"adults":0,"children":0}')
          const totalGuests = (guestsData.adults || 0) + (guestsData.children || 0)
          this.hasGuests = totalGuests > 0
        } catch (e) {
          this.hasGuests = false
        }
        
        if (!this.hasGuests) {
          try {
            const conservationData = JSON.parse(localStorage.getItem('conservation_data') || '[]')
            const totalGuests = conservationData.reduce((total, fee) => {
              return total + (Number(fee.payment_guests) || 0)
            }, 0)
            this.hasGuests = totalGuests > 0
          } catch (e) {
            this.hasGuests = false
          }
        }
        
        try {
          const vehicleData = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
          this.hasVehicles = vehicleData.some(vehicle => Number(vehicle.vehicles) > 0)
        } catch (e) {
          this.hasVehicles = false
        }
        
        try {
          const vehicleSeparate = localStorage.getItem('vehicle_separate')
          if (vehicleSeparate !== null) {
            this.vehicleFeeSeparate = JSON.parse(vehicleSeparate)
          }
        } catch (e) {
          this.vehicleFeeSeparate = false
        }
        
        if (this.$store.state && this.$store.state.VEHICLE_SEPARATE !== undefined) {
          this.vehicleFeeSeparate = this.$store.state.VEHICLE_SEPARATE || this.vehicleFeeSeparate
        }
        
        const vehiclesOk = this.hasVehicles || this.vehicleFeeSeparate
        this.showViewCartButton = this.hasGuests && vehiclesOk
        
        if (this.showViewCartButton && this.currentStep < 4) {
          this.currentStep = 4
        }
      },
      
      // ============ DATE SELECTION METHODS ============
      
      onCheckInSelected() {
        this.updateCheckOutMin()
        this.saveDatesToGlobalStore()
        this.updateCartTotal()
        
        setTimeout(() => {
          this.$refs.dialog.save(this.booking.check_in)
          this.modal1 = false
          this.advanceStep()
        }, 300)
        
        this.showAutoAddNotification('📅 Check-in date selected')
      },
      
      onCheckOutSelected() {
        if (this.booking.check_in) {
          var a = this.$moment(this.booking.check_in)
          var b = this.$moment(this.booking.check_out)
          this.booking.days = b.diff(a, 'days')
        }
        
        this.retrievePricingSchedule()
        this.saveDatesToGlobalStore()
        this.updateCartTotal()
        
        setTimeout(() => {
          this.$refs.dialog1.save(this.booking.check_out)
          this.modal2 = false
          this.advanceStep()
        }, 300)
        
        this.showAutoAddNotification('📅 Check-out date selected')
      },
      
      saveCheckIn() {
        this.$refs.dialog.save(this.booking.check_in)
        this.updateCheckOutMin()
        this.saveDatesToGlobalStore()
        this.updateCartTotal()
        this.showAutoAddNotification('📅 Check-in date saved')
        this.advanceStep()
      },
      
      saveCheckOut() {
        this.$refs.dialog1.save(this.booking.check_out)
        if (this.booking.check_in) {
          var a = this.$moment(this.booking.check_in)
          var b = this.$moment(this.booking.check_out)
          this.booking.days = b.diff(a, 'days')
        }
        this.retrievePricingSchedule()
        this.saveDatesToGlobalStore()
        this.updateCartTotal()
        this.showAutoAddNotification('📅 Check-out date saved')
        this.advanceStep()
      },
      
      // ============ CART METHODS ============
      
      updateCartTotal() {
        const facilities = JSON.parse(localStorage.getItem('facility_data') || '[]')
        const vehicles = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        const activities = JSON.parse(localStorage.getItem('activity_data') || '[]')
        
        let total = 0
        if (conservation.length > 0) total += 1
        if (vehicles.length > 0) total += 1
        
        const uniqueFacilities = new Set()
        facilities.forEach(f => {
          uniqueFacilities.add(f.id || f.public_id || f.facility_id || f.name)
        })
        total += uniqueFacilities.size
        
        const uniqueActivities = new Set()
        activities.forEach(a => {
          const key = `${a.name || a.activity_name}_${a.date}_${a.time || a.start_time}`
          uniqueActivities.add(key)
        })
        total += uniqueActivities.size
        
        this.cartTotal = total
      },
      
      handleGuestsUpdated(guestsData) {
        this.updateCartTotal()
        this.showAutoAddNotification('👥 Guests updated')
        if (guestsData && guestsData.total > 0) {
          this.hasGuests = true
        } else {
          this.hasGuests = false
        }
        this.checkViewCartButtonVisibility()
        if (this.hasGuests && this.currentStep < 3) {
          this.advanceStep()
        }
      },
      
      handleVehiclesUpdated(vehiclesData) {
        this.updateCartTotal()
        this.showAutoAddNotification('🚗 Vehicles updated')
        if (vehiclesData) {
          this.hasVehicles = vehiclesData.total > 0
          this.vehicleFeeSeparate = vehiclesData.vehicleFeeSeparate || false
        }
        this.checkViewCartButtonVisibility()
        if ((this.hasVehicles || this.vehicleFeeSeparate) && this.currentStep < 4) {
          this.advanceStep()
        }
      },
      
      showAutoAddNotification(message) {
        this.$root.$emit('show-notification', {
          message: message,
          type: 'success',
          timeout: 2000
        })
      },
      viewCart: function () {
  if (this.booking.check_in === '' || this.booking.check_out === '') {
    this.snackbar = true
  } else {
    // ❌ REMOVE or COMMENT OUT these lines:
    // this.adjustDates(this.booking.check_in, this.booking.check_out)
    // this.storeConservation()
    // this.storeVehicles()
    // this.saveDatesToGlobalStore()
    
    // ✅ ONLY open the sidebar
    this.$root.$emit('open-sidebar')
  }
},

      // ============ SCROLL TO TABS ============
scrollToTabs() {
  setTimeout(() => {
    const tabsElement = document.querySelector('.main-content')
    
    if (tabsElement) {
      const rect = tabsElement.getBoundingClientRect()
      const scrollTarget = window.pageYOffset + rect.top + 500
      
      if (rect.top > 100 || rect.top < -100) {
        window.scrollTo({
          top: scrollTarget,
          behavior: 'smooth'
        })
      } else {
        window.scrollBy({
          top: 800,
          behavior: 'smooth'
        })
      }
    } else {
      window.scrollBy({
        top: 1000,
        behavior: 'smooth'
      })
    }
  }, 100)
},
  
      clearAllBookingData: function() {
        this.booking = {
          days: 0,
          check_in: '',
          check_out: '',
          currency: localStorage.getItem('currency')
        };
        this.minCheckOutDate = this.start_date;
        this.currentStep = 0;
        this.showViewCartButton = false;
        localStorage.removeItem('booking');
        localStorage.removeItem('conservation_data');
        localStorage.removeItem('vehicle_data');
        localStorage.removeItem('guests');
        localStorage.removeItem('guests_data');
        
        if (this.$store.dispatch && this.$store.state.GlobalBookingStore) {
          this.$store.dispatch('GlobalBookingStore/updateBookingDates', {
            check_in: '',
            check_out: '',
            days: 0
          });
        }
        
        if (this.$store.commit && this.$store.state.GuestsStore) {
          this.$store.commit('GuestsStore/SET_GUESTS', 'Number of Guests');
          if (this.$store.commit('GuestsStore/SET_GUEST_AGES')) {
            this.$store.commit('GuestsStore/SET_GUEST_AGES', []);
          }
        }
        
        if (this.$store.commit) {
          this.$store.commit('LOCAL_VEHICLES', []);
          this.$store.commit('LOCAL_CONSERVATION', []);
        }
      },
      
      loadDatesFromGlobalStore() {
        if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_dates) {
          const globalDates = this.$store.state.GlobalBookingStore.booking_dates
          if (globalDates.check_in && globalDates.check_in !== '' && 
              globalDates.check_out && globalDates.check_out !== '') {
            this.booking.check_in = globalDates.check_in
            this.booking.check_out = globalDates.check_out
            this.booking.days = globalDates.days || 0
            this.updateCheckOutMin()
          }
        }
      },
      
      saveDatesToGlobalStore() {
        if (this.booking.check_in && this.booking.check_in !== '' && 
            this.booking.check_out && this.booking.check_out !== '') {
          const dates = {
            check_in: this.booking.check_in,
            check_out: this.booking.check_out,
            days: this.booking.days
          }
          if (this.$store.dispatch && this.$store.state.GlobalBookingStore) {
            this.$store.dispatch('GlobalBookingStore/updateBookingDates', dates)
          }
        }
      },
      
      storeVehicles: function () {
        localStorage.setItem('vehicle_data', JSON.stringify(this.allVehicles))
      },
      storeConservation: function () {
        localStorage.setItem('conservation_data', JSON.stringify(this.allConservationFees))
      },
      setCurrency: function (data) {
        this.booking.currency = data
        localStorage.setItem('currency', data)
        this.$store.state.CurrencyStore.browser_currency = data
        this.saveDatesToGlobalStore()
      },
      putStartDate: function () {
        var date = this.booking.check_in
        date = this.$moment(date).format('YYYY-MM-DD')
        this.booking.check_in = date
        this.saveDatesToGlobalStore()
      },
      putEndDate: function () {
        var date = this.booking.check_out
        date = this.$moment(date).format('YYYY-MM-DD')
        this.booking.check_out = date
        var a = this.$moment(this.$moment(this.booking.check_in).format('YYYY, MM, DD'))
        var b = this.$moment(this.$moment(this.booking.check_out).format('YYYY, MM, DD'))
        this.booking.days = b.diff(a, 'days')
        this.saveDatesToGlobalStore()
      },
      retrievePricingSchedule: function () {
        let data = {
          check_in_date: this.booking.check_in,
          check_out_date: this.booking.check_out
        }
        this.$store.dispatch('get_conservation_fee_by_check_in_out_date', data)
      },
      updateCheckOutMin: function () {
        if (this.booking.check_in && this.booking.check_in !== '') {
          this.minCheckOutDate = this.$moment(this.booking.check_in).add(0, 'days').format('YYYY-MM-DD')
          if (this.booking.check_out && this.$moment(this.booking.check_out).isBefore(this.minCheckOutDate)) {
            this.booking.check_out = this.minCheckOutDate
            this.saveDatesToGlobalStore()
          }
        } else {
          this.minCheckOutDate = this.start_date
        }
      }
    },
    watch: {
      'booking.check_in': {
        handler: function(newVal) {
          if (newVal && newVal !== '') {
            this.saveDatesToGlobalStore()
          }
        },
        deep: true
      },
      'booking.check_out': {
        handler: function(newVal) {
          if (newVal && newVal !== '') {
            this.saveDatesToGlobalStore()
          }
        },
        deep: true
      },
      'booking.days': {
        handler: function(newVal) {
          if (newVal && newVal > 0) {
            this.saveDatesToGlobalStore()
          }
        },
        deep: true
      }
    }
  }
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('PizzaDude.dk-Earthwerk.otf') format('opentype');
    font-weight: normal;
    font-style: normal;
  }

  .transparent-search {
    background-color: rgba(255, 255, 255, 0.2) !important;
    backdrop-filter: blur(5px);
    border-radius: 12px !important;
    padding: 15px;
  }

  .search-container {
    margin: 0 auto;
    max-width: 1200px;
  }

  .earthwerk-font {
    font-family: 'Earthwerk', sans-serif;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .input-field {
    font-size: 1.1rem !important;
  }

  .input-field >>> .v-label {
    font-family: 'Earthwerk', sans-serif !important;
    font-size: 0.9rem !important;
    opacity: 0.9 !important;
  }

  .action-button {
    border-radius: 6px !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    font-weight: bold;
    height: 48px !important;
    font-size: 1.1rem;
    letter-spacing: 1.5px;
  }

  .v-btn {
    font-family: 'Earthwerk', sans-serif !important;
    text-transform: uppercase !important;
  }

  /* ============ STEP INDICATOR STYLES ============ */
  .step-indicator {
    padding: 8px 16px 12px 16px;
    margin-bottom: 4px;
  }

  .step-dots {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    margin-bottom: 4px;
    flex-wrap: wrap;
  }

  .step-dot {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: default;
    flex: 1;
    position: relative;
    min-width: 40px;
  }

  .step-dot.clickable {
    cursor: pointer;
  }

  .step-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;
    color: #999;
    background: #e0e0e0;
    transition: all 0.3s ease;
    font-family: 'Earthwerk', sans-serif;
  }

  .step-dot.active .step-number {
    background: #74a533;
    color: white;
    box-shadow: 0 0 0 4px rgba(116, 165, 51, 0.3);
    transform: scale(1.1);
  }

  .step-dot.completed .step-number {
    background: #74a533;
    color: white;
  }

  .step-dot.completed .step-number::after {
    content: '✓';
    font-size: 12px;
  }

  .step-dot.completed .step-number span {
    display: none;
  }

  .step-label {
    font-size: 9px;
    color: #999;
    margin-top: 4px;
    font-family: 'Earthwerk', sans-serif;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    transition: color 0.3s ease;
    white-space: nowrap;
  }

  .step-dot.active .step-label {
    color: #74a533;
    font-weight: bold;
  }

  .step-dot.completed .step-label {
    color: #74a533;
  }

  .step-progress {
    margin-top: 4px;
  }

  /* ============ STEP HIGHLIGHT STYLES ============ */
  .step-wrapper {
    position: relative;
    transition: all 0.3s ease;
  }

  .step-highlight {
    animation: highlightPulse 1.5s ease-in-out 2;
  }

  @keyframes highlightPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(116, 165, 51, 0);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 20px rgba(116, 165, 51, 0.3);
    }
  }

  .step-badge {
    position: absolute;
    top: -6px;
    left: -6px;
    background: #74a533;
    color: white;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: bold;
    z-index: 10;
    font-family: 'Earthwerk', sans-serif;
    animation: badgePop 0.3s ease-out;
  }

  @keyframes badgePop {
    0% {
      transform: scale(0);
    }
    80% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  .step-hint {
    font-size: 20px;
    color: #f26130;
    text-align: center;
    margin-top: 2px;
    font-family: 'Earthwerk', sans-serif;
    letter-spacing: 0.5px;
    animation: fadeInUp 0.3s ease-out;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* ============ ARROW GUIDE ============ */
  .arrow-guide {
    padding: 12px 16px 4px 16px;
    animation: fadeInUp 0.5s ease-out;
    cursor: pointer;
  }

  .arrow-guide:hover .arrow-down {
    background: rgba(116, 165, 51, 0.15);
    transform: scale(1.01);
    transition: all 0.3s ease;
  }

  .arrow-down {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 12px 20px;
    background: rgba(255, 255, 255, 0.85);
    border-radius: 10px;
    border: 2px solid #74a533;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
  }

  .arrow-icon {
    font-size: 28px;
    color: #74a533;
    animation: bounceDown 2s infinite;
  }

  @keyframes bounceDown {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(8px);
    }
  }

  .arrow-text {
    font-size: 14px;
    color: #2d2d2d;
    text-align: center;
    font-family: 'Earthwerk', sans-serif;
    letter-spacing: 0.5px;
    line-height: 1.5;
    font-weight: 500;
  }

  /* ============ RESPONSIVE ============ */
  
  /* Tablet */
  @media (max-width: 960px) {
    .transparent-search {
      padding: 10px;
    }

    .input-field {
      font-size: 1rem !important;
    }

    .action-button {
      font-size: 1rem;
    }

    .step-dots {
      gap: 2px;
    }

    .step-dot {
      flex: 0 0 auto;
      padding: 0 4px;
      min-width: 32px;
    }

    .step-label {
      font-size: 7px;
    }

    .step-number {
      width: 22px;
      height: 22px;
      font-size: 9px;
    }

    .arrow-icon {
      font-size: 22px;
    }

    .arrow-text {
      font-size: 12px;
    }

    .arrow-down {
      padding: 10px 16px;
    }
  }

  /* Mobile */
  @media (max-width: 600px) {
    .transparent-search {
      padding: 8px;
    }

    .input-field {
      font-size: 0.9rem !important;
    }

    .action-button {
      font-size: 0.9rem;
      letter-spacing: 1px;
    }

    .step-indicator {
      padding: 4px 8px 8px 8px;
    }

    .step-dots {
      justify-content: center;
      gap: 2px;
    }

    .step-dot {
      padding: 0 3px;
      min-width: 28px;
    }

    .step-label {
      display: none;
    }

    .step-number {
      width: 18px;
      height: 18px;
      font-size: 8px;
    }

    .step-hint {
      font-size: 20px;
    }

    .step-badge {
      width: 16px;
      height: 16px;
      font-size: 8px;
      top: -4px;
      left: -4px;
    }

    .arrow-guide {
      padding: 8px 8px 2px 8px;
    }

    .arrow-down {
      padding: 8px 12px;
    }

    .arrow-icon {
      font-size: 18px;
    }

    .arrow-text {
      font-size: 10px;
    }
  }

  /* Very Small Phones */
  @media (max-width: 360px) {
    .arrow-text {
      font-size: 9px;
    }

    .arrow-icon {
      font-size: 16px;
    }

    .arrow-down {
      padding: 6px 10px;
    }
  }

  /* Landscape Phones */
  @media (max-height: 500px) and (orientation: landscape) {
    .arrow-guide {
      padding: 4px 8px 2px 8px;
    }

    .arrow-down {
      padding: 6px 14px;
      flex-direction: row;
      gap: 10px;
    }

    .arrow-icon {
      font-size: 16px;
    }

    .arrow-text {
      font-size: 10px;
    }
  }
</style>
